import socialApiClient from "../helpers/services/social-api-client";
import {
  buildSocialPostImagePrompt,
  openAiSizeForAspect,
  SocialPostImageAspect,
  SocialPostImageTextDensity,
} from "../../ui/component/socialPost/modals/buildSocialPostImagePrompt";
import { toast } from "react-toastify";
import { WEMBII_AI_CREDITS_REFRESH_EVENT } from "../../constants/dom.constant";

export type { SocialPostImageAspect, SocialPostImageTextDensity };

/**
 * Background generation manager for "Publicaciones IA".
 *
 * Créditos IA:
 * - Solo texto → 1 crédito (openai/posts).
 * - Solo diseño → 1 crédito (openai/images).
 * - Texto + diseño → 2 créditos (openai/images con creditAmount: 2).
 * - Edición de texto posterior → gratis (límite mensual por post en backend).
 * - Edición de imagen → 1 crédito (openai/images).
 */

export type AutoPostContentType = "text" | "design" | "both";

export type AutoPostGenTask = {
  id: string;
  topicLabel: string;
  socialMedia: string;
  /** Reference photo for this specific post (optional). */
  referenceFile: File | null;
};

export type AutoPostImageResult = {
  file: File;
  previewUrl: string;
  /** Durable S3 (or similar) URL when the API persisted the image. */
  durableUrl?: string;
};

export type AutoPostResult = {
  status: "pending" | "done" | "error";
  image?: AutoPostImageResult;
  text?: string;
  error?: string;
};

export type AutoPostJobStatus =
  | "idle"
  | "running"
  | "stopped"
  | "done"
  | "error";

export type AutoPostJobState = {
  status: AutoPostJobStatus;
  total: number;
  done: number;
  startedAt: number;
  endedAt: number | null;
  projectName: string;
  results: Record<string, AutoPostResult>;
};

export type StartJobParams = {
  jobKey: string;
  companyId: string;
  industryLabel: string;
  additionalInfo: string;
  /** Imágenes de inspiración / moodboard para la línea gráfica. */
  marketingLineFiles?: File[];
  brandName: string;
  contentType: AutoPostContentType;
  hasOpenAi: boolean;
  logoFile: File | null;
  projectName: string;
  posts: AutoPostGenTask[];
  imageAspect?: SocialPostImageAspect;
  imageTextDensity?: SocialPostImageTextDensity;
};

type Listener = (state: AutoPostJobState) => void;

const jobs = new Map<string, AutoPostJobState>();
const abortFlags = new Map<string, boolean>();
const listeners = new Map<string, Set<Listener>>();

const OPENAI_IMAGES_ENDPOINT = "openai/images";
const OPENAI_POSTS_ENDPOINT = "openai/posts";
const IMAGE_PROMPT_MAX_CHARS = 18000;

function apiErrorMessage(error: any): string {
  const data = error?.response?.data;
  const raw = data?.message ?? error?.message;
  if (Array.isArray(raw)) return raw.filter(Boolean).join(" ");
  return String(raw || "No se pudo generar la publicación.");
}

function isCreditAmountRejected(error: any): boolean {
  if (error?.response?.status !== 400) return false;
  return apiErrorMessage(error).toLowerCase().includes("creditamount");
}

function emit(jobKey: string) {
  const state = jobs.get(jobKey);
  if (!state) return;
  const set = listeners.get(jobKey);
  if (!set) return;
  // Emit a shallow clone so subscribers relying on reference equality re-render.
  const snapshot: AutoPostJobState = {
    ...state,
    results: { ...state.results },
  };
  set.forEach((cb) => {
    try {
      cb(snapshot);
    } catch {
      // ignore subscriber errors
    }
  });
}

function base64ToBlob(b64: string, mimeType: string): Blob {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mimeType });
}

async function generateImageForTask(
  params: StartJobParams,
  task: AutoPostGenTask
): Promise<AutoPostImageResult> {
  const marketingLineFiles = (params.marketingLineFiles || []).filter(Boolean);
  const hasMarketingLine = marketingLineFiles.length > 0;
  const imageAspect = params.imageAspect || "1:1";
  const imageTextDensity = params.imageTextDensity || "minimal";
  const imageSize = openAiSizeForAspect(imageAspect);
  const imageCreditAmount =
    params.contentType === "both" ? 2 : 1;

  const basePrompt = buildSocialPostImagePrompt({
    industry: params.industryLabel,
    subject: task.topicLabel,
    additionalInfo: params.additionalInfo,
    hasMarketingLineRefs: hasMarketingLine,
    platform: task.socialMedia,
    brandName: params.brandName,
    aspectRatio: imageAspect,
    textDensity: imageTextDensity,
  });

  const noCalendarMonthRule = [
    "REGLA CRÍTICA PARA ESTA PIEZA:",
    "- No escribas ni ilustrés el mes de publicación ni ninguna fecha de calendario del plan (p. ej. agosto, septiembre, 2026).",
    "- El calendario de redes es interno: la imagen no debe mencionarlo.",
    imageTextDensity === "minimal"
      ? "- Usa poco texto: 1 titular corto y como máximo 1 línea extra; nada redundante ni párrafos."
      : "- Podés incluir titular, beneficio y CTA, con jerarquía clara y sin saturar.",
  ].join("\n");

  // Orden de adjuntos: foto de contenido → moodboard de línea de mkt → logo (último).
  const references = [
    task.referenceFile,
    ...marketingLineFiles,
    params.logoFile,
  ].filter((file): file is File => Boolean(file && file.size > 0));

  const attachmentRules: string[] = [noCalendarMonthRule];
  if (hasMarketingLine) {
    attachmentRules.push(
      [
        "REFERENCIAS DE LÍNEA DE MARKETING (MOODBOARD):",
        "- Hay imágenes adjuntas de inspiración gráfica (línea de marketing).",
        "- Úsalas solo como guía de estilo: colores, tipografía, atmósfera, composición y look & feel.",
        "- No copies textos, logos ajenos ni el contenido literal de esas referencias.",
        "- Todas las piezas deben sentirse de la misma familia visual gracias a esa línea.",
      ].join("\n")
    );
  }
  if (params.logoFile) {
    attachmentRules.push(
      [
        "REFERENCIA DE MARCA OBLIGATORIA:",
        "- El último archivo adjunto es el logo de la marca.",
        "- Incorpóralo de forma visible, legible y proporcionada en la pieza final.",
        "- Conserva sus colores, forma y texto; no lo rediseñes, deformes ni sustituyas.",
      ].join("\n")
    );
  }

  const prompt = [basePrompt, "", ...attachmentRules]
    .join("\n\n")
    .slice(0, IMAGE_PROMPT_MAX_CHARS);

  const requestOpts = {
    params: { companyId: params.companyId },
    timeout: 240000,
  };

  const postImageRequest = async (includeCreditAmount: boolean) => {
    if (references.length) {
      const formData = new FormData();
      formData.append("prompt", prompt);
      formData.append("size", imageSize);
      if (includeCreditAmount && imageCreditAmount > 1) {
        formData.append("creditAmount", String(imageCreditAmount));
      }
      references.forEach((file) => formData.append("images", file));
      return socialApiClient().post(OPENAI_IMAGES_ENDPOINT, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        ...requestOpts,
      });
    }
    return socialApiClient().post(
      OPENAI_IMAGES_ENDPOINT,
      {
        prompt,
        size: imageSize,
        ...(includeCreditAmount && imageCreditAmount > 1
          ? { creditAmount: imageCreditAmount }
          : {}),
      },
      requestOpts
    );
  };

  let response;
  try {
    response = await postImageRequest(true);
  } catch (error: any) {
    if (imageCreditAmount > 1 && isCreditAmountRejected(error)) {
      response = await postImageRequest(false);
    } else {
      const status = error?.response?.status;
      const msg = apiErrorMessage(error).toLowerCase();
      if (msg.includes("insufficient ai credits")) {
        throw new Error(
          "No hay créditos de IA suficientes para generar esta publicación."
        );
      }
      if (
        status === 504 ||
        status === 502 ||
        status === 408 ||
        msg.includes("504") ||
        msg.includes("timeout") ||
        msg.includes("timed out")
      ) {
        throw new Error(
          "La IA tardó demasiado (timeout). Esperá un momento y volvé a generar."
        );
      }
      throw new Error(apiErrorMessage(error));
    }
  }

  notifyAiCreditsRefresh();

  const payload = response.data?.payload ?? response.data;
  const b64 = payload?.b64_json;
  const imageUrl =
    typeof payload?.url === "string" ? payload.url.trim() : "";
  const mimeType = payload?.mimeType || "image/png";
  const isEphemeralOpenAiUrl =
    imageUrl.includes("oaidalleapiprodscus") ||
    imageUrl.includes("openai.com");
  const durableUrl =
    imageUrl.startsWith("http") && !isEphemeralOpenAiUrl ? imageUrl : undefined;

  let blob: Blob;
  if (b64) {
    blob = base64ToBlob(b64, mimeType);
  } else if (imageUrl) {
    const res = await fetch(imageUrl);
    if (!res.ok) {
      throw new Error("No se pudo descargar la imagen generada.");
    }
    blob = await res.blob();
  } else {
    throw new Error("OpenAI no devolvió una imagen para la publicación.");
  }

  const ext = (blob.type || mimeType).includes("png")
    ? "png"
    : (blob.type || mimeType).includes("webp")
    ? "webp"
    : "jpg";
  const file = new File(
    [blob],
    `openai-post-${Date.now()}-${task.id}.${ext}`,
    { type: blob.type || mimeType }
  );
  // Prefer durable URL for preview/persist so refresh doesn't lose the image.
  const previewUrl = durableUrl || URL.createObjectURL(file);
  return { file, previewUrl, durableUrl };
}

function notifyAiCreditsRefresh() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(WEMBII_AI_CREDITS_REFRESH_EVENT));
  }
}

function buildCaptionAdditionalInfo(
  industryLabel: string,
  themeLabel: string,
  extraInfo: string,
  brandName?: string
): string {
  const parts = [
    brandName?.trim()
      ? `Empresa/marca: "${brandName.trim()}".`
      : "",
    `Identidad/rubro de ESTE cliente: "${industryLabel}". Tema: "${themeLabel}".`,
    `Habla ÚNICAMENTE de este negocio y temática. No inventes otro rubro (moda, retail ajeno, etc.) ni mezcles identidad de otros clientes.`,
    `Usa el nombre comercial de la empresa cuando menciones la marca. Nunca uses el nombre interno del proyecto IA ni IDs.`,
    `No uses placeholders como #TuIndustria, #TuMarca o [industria].`,
    `Tu tarea: escribir SOLO el pie de publicación (caption) listo para publicar en la red social indicada.`,
    `Estilo: como un posteo real de esta marca en redes sociales, en español natural, cercano y persuasivo.`,
    `Incluí emojis relevantes (2 a 5) y 2 a 4 hashtags útiles al final (relevantes a este rubro, sin placeholders).`,
    `Estructura sugerida: gancho con emoji → 1 o 2 frases de beneficio concretas → CTA claro → hashtags.`,
    `Longitud: 3 a 6 líneas cortas; no suene genérico ni robótico; no menciones ChatGPT, OpenAI ni que es contenido generado por IA.`,
  ];
  const extra = (extraInfo || "").trim();
  if (extra) {
    parts.push(`Información adicional del negocio a considerar: ${extra}`);
  }
  return parts.filter(Boolean).join(" ");
}

async function generateTextForTask(
  params: StartJobParams,
  task: AutoPostGenTask,
  options?: { consumeCredit?: boolean }
): Promise<string> {
  const body = {
    industry: params.industryLabel,
    subject: `${task.topicLabel} · ${params.industryLabel}`,
    emoticons: true,
    hashtags: true,
    additionalInfo: buildCaptionAdditionalInfo(
      params.industryLabel,
      task.topicLabel,
      params.additionalInfo,
      params.brandName
    ),
    socialMedias: [task.socialMedia],
    mode: "single" as const,
    ...(options?.consumeCredit ? { consumeCredit: 1 } : {}),
  };

  let data: any;
  try {
    const res = await socialApiClient().post(OPENAI_POSTS_ENDPOINT, body, {
      params: { companyId: params.companyId },
      timeout: 90000,
    });
    data = res.data;
  } catch (error: any) {
    const msg = String(
      error?.response?.data?.message || error?.message || ""
    ).toLowerCase();
    if (msg.includes("insufficient ai credits")) {
      throw new Error(
        "No hay créditos de IA suficientes para generar esta publicación."
      );
    }
    throw error;
  }

  if (options?.consumeCredit) {
    notifyAiCreditsRefresh();
  }

  const payload = data?.payload ?? data;
  const templates = Array.isArray(payload?.templates)
    ? payload.templates
    : payload?.template
    ? [payload.template]
    : Array.isArray(data?.templates)
    ? data.templates
    : [];
  const first = templates[0] as { body?: string; text?: string } | undefined;
  const content =
    first?.body ||
    first?.text ||
    (typeof payload?.body === "string" ? payload.body : "") ||
    "";
  const out = typeof content === "string" ? content.trim() : "";
  if (!out) {
    throw new Error("La IA de Wembii no devolvió texto para el post.");
  }
  return out;
}

async function runJob(params: StartJobParams) {
  const { jobKey, contentType, hasOpenAi } = params;
  const wantsImage = contentType === "design" || contentType === "both";
  const wantsText =
    (contentType === "text" || contentType === "both") && hasOpenAi;
  const chargeTextCredit = wantsText && contentType === "text";

  for (let i = 0; i < params.posts.length; i += 1) {
    if (abortFlags.get(jobKey)) break;

    const task = params.posts[i];
    const state = jobs.get(jobKey);
    if (!state) return;

    const result: AutoPostResult = { status: "pending" };
    state.results[task.id] = result;

    try {
      let imageError: string | undefined;
      if (wantsImage) {
        try {
          result.image = await generateImageForTask(params, task);
        } catch (imgError: any) {
          imageError = apiErrorMessage(imgError);
        }
      }
      if (abortFlags.get(jobKey)) break;
      if (wantsText) {
        try {
          result.text = await generateTextForTask(params, task, {
            consumeCredit: chargeTextCredit,
          });
        } catch (textError: any) {
          result.text = undefined;
          result.error = apiErrorMessage(textError);
        }
      }
      if (imageError) {
        result.error = imageError;
      }
      if (result.image || result.text) {
        result.status = "done";
      } else {
        result.status = "error";
        result.error =
          result.error || imageError || "No se pudo generar la publicación.";
      }
    } catch (error: any) {
      result.status = "error";
      result.error = apiErrorMessage(error);
    } finally {
      const current = jobs.get(jobKey);
      if (current) {
        current.done += 1;
        current.results[task.id] = result;
        emit(jobKey);
      }
    }
  }

  const finalState = jobs.get(jobKey);
  if (finalState) {
    finalState.status = abortFlags.get(jobKey) ? "stopped" : "done";
    finalState.endedAt = Date.now();
    emit(jobKey);
    if (finalState.status === "done") {
      const failed = Object.values(finalState.results).filter(
        (r) => r.status === "error"
      ).length;
      const projectLabel = finalState.projectName || "tu proyecto";
      if (failed === 0) {
        toast.success(
          `El contenido de "${projectLabel}" está listo para revisar.`
        );
      } else if (failed >= finalState.total) {
        toast.error(
          `No se pudo generar el contenido de "${projectLabel}". Revisá créditos o reintentá.`
        );
      } else {
        toast.warning(
          `"${projectLabel}" listo con advertencias: ${failed} de ${finalState.total} posts fallaron.`
        );
      }
      // Campanita: siempre al terminar la generación.
      void socialApiClient()
        .post(
          "openai/auto-posts/ready-notification",
          {
            projectName: finalState.projectName || "",
            total: finalState.total,
            done: finalState.done,
            failed,
          },
          { params: { companyId: params.companyId } }
        )
        .catch((err) => {
          console.error(
            "[autoPostGenerationManager] ready-notification failed",
            err?.message || err
          );
        });
    }
  }
  abortFlags.delete(jobKey);
}

export const autoPostGenerationManager = {
  getState(jobKey: string): AutoPostJobState | null {
    return jobs.get(jobKey) ?? null;
  },

  isRunning(jobKey: string): boolean {
    return jobs.get(jobKey)?.status === "running";
  },

  subscribe(jobKey: string, cb: Listener): () => void {
    let set = listeners.get(jobKey);
    if (!set) {
      set = new Set();
      listeners.set(jobKey, set);
    }
    set.add(cb);
    return () => {
      set?.delete(cb);
    };
  },

  stop(jobKey: string) {
    if (jobs.get(jobKey)?.status === "running") {
      abortFlags.set(jobKey, true);
      const state = jobs.get(jobKey);
      if (state) {
        state.status = "stopped";
        emit(jobKey);
      }
    }
  },

  clear(jobKey: string) {
    const state = jobs.get(jobKey);
    if (state) {
      Object.values(state.results).forEach((r) => {
        if (r.image?.previewUrl?.startsWith("blob:")) {
          URL.revokeObjectURL(r.image.previewUrl);
        }
      });
    }
    jobs.delete(jobKey);
    abortFlags.delete(jobKey);
  },

  start(params: StartJobParams): AutoPostJobState {
    const existing = jobs.get(params.jobKey);
    if (existing && existing.status === "running") {
      return existing;
    }

    // Reset any previous results/URLs for a clean re-run.
    if (existing) {
      Object.values(existing.results).forEach((r) => {
        if (r.image?.previewUrl?.startsWith("blob:")) {
          URL.revokeObjectURL(r.image.previewUrl);
        }
      });
    }

    const state: AutoPostJobState = {
      status: "running",
      total: params.posts.length,
      done: 0,
      startedAt: Date.now(),
      endedAt: null,
      projectName: params.projectName,
      results: {},
    };
    jobs.set(params.jobKey, state);
    abortFlags.set(params.jobKey, false);
    emit(params.jobKey);

    // Fire-and-forget: keeps running independent of React lifecycle.
    void runJob(params);

    return state;
  },
};
